"use client"

import React, { useMemo, useState } from "react"
import { ChevronDown, ChevronRight, Loader2, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Blade, PillButton } from "@/components/blades/blade"
import { FilterValueControl, OperatorSelect, useReferenceSets } from "@/components/explorer/filter-controls"
import { useExplorerContext } from "@/components/explorer/explorer-context"
import { groupFilterDefinitions } from "@/lib/filter-groups"
import { activeFilterCount, hasFilterState, operatorOf, withFilterOperator, withFilterValues, withoutFilter } from "@/lib/filter-values"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsFilterDefinition } from "@/types/ats-explorer-api"

export interface FilterBladeProps {
  open: boolean
  onClose: () => void
  depth?: number
  trail?: string[]
  onTrailClick?: (index: number) => void
  /** Scrolls this filter into view and opens its group */
  focusName?: string | null
}

/**
 * The Timesheet Hub's filter drawer for the explorer: every filter the entity offers,
 * in sections (identity, dates, people & companies, sort, other, custom), each as a
 * label · operator · value row. Changes apply live — the chip row and the records
 * follow as you edit — so the footer only offers Clear and Done.
 */
export function FilterBlade({ open, onClose, depth, trail, onTrailClick, focusName }: FilterBladeProps) {
  const { atsSystem, activeEntity, isLoadingEntityDetail, filterDefinitions, filterValues, setFilterValues, clearFilters } = useExplorerContext()
  const { t, n } = useTranslation()
  const [query, setQuery] = useState("")
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const groups = useMemo(() => groupFilterDefinitions(filterDefinitions, query), [filterDefinitions, query])
  const referenceSets = useReferenceSets(atsSystem ?? "", filterDefinitions)
  const activeCount = activeFilterCount(filterValues)

  const isGroupOpen = (key: string, collapsed: boolean, definitions: AtsFilterDefinition[]) => {
    if (key in openGroups) return openGroups[key]
    if (focusName && definitions.some((d) => d.name === focusName)) return true
    // A group holding an active filter opens itself
    return !collapsed || definitions.some((d) => hasFilterState(filterValues[d.name]))
  }

  if (!atsSystem) return null

  return (
    <Blade
      open={open}
      onClose={onClose}
      size="sm"
      depth={depth}
      trail={trail}
      onTrailClick={onTrailClick}
      eyebrow={activeEntity?.displayName}
      title={t("explorer.filters.title")}
      badges={
        activeCount > 0 ? (
          <span className="rounded-pill bg-accent px-2.5 py-0.5 text-xs font-bold text-accent-foreground">
            {t((activeCount === 1 ? "explorer.filters.activeOne" : "explorer.filters.activeOther"), { count: n(activeCount) })}
          </span>
        ) : undefined
      }
      bare
      footer={
        <>
          <PillButton variant="ghost" onClick={clearFilters} disabled={activeCount === 0}>
            {t("explorer.common.clear")}
          </PillButton>
          <PillButton onClick={onClose}>{t("common.actions.done")}</PillButton>
        </>
      }
    >
      <div className="sticky top-0 z-10 border-b border-card-border bg-card px-6 py-3">
        <Input
          className="h-8 text-xs"
          placeholder={t("explorer.filters.searchPlaceholder", { count: n(filterDefinitions.length) })}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        {isLoadingEntityDetail && (
          <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> {t("explorer.filters.loadingComplete")}
          </p>
        )}
      </div>

      <div className="px-6">
        {groups.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground">{t("explorer.filters.noMatches")}</p>}
        {groups.map((group) => {
          const isOpen = isGroupOpen(group.key, group.collapsed, group.definitions)
          const activeInGroup = group.definitions.filter((d) => hasFilterState(filterValues[d.name])).length
          return (
            <section key={group.key} className="border-b border-[var(--th-menu-border)] py-3 last:border-b-0">
              <button
                type="button"
                className="flex w-full items-center gap-1 py-1 text-left"
                onClick={() => setOpenGroups((current) => ({ ...current, [group.key]: !isOpen }))}
              >
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                <span className="th-section-label">{t(group.titleKey)}</span>
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {activeInGroup > 0 ? `${t("explorer.filters.groupActive", { count: n(activeInGroup) })} · ` : ""}
                  {n(group.definitions.length)}
                </span>
              </button>
              {isOpen && (
                <div className="mt-2 space-y-3">
                  {group.definitions.map((definition) => (
                    <FilterRow
                      key={definition.name}
                      atsSystem={atsSystem}
                      definition={definition}
                      highlighted={focusName === definition.name}
                      referenceItems={referenceSets[definition.referenceType ?? ""] ?? []}
                      draft={drafts[definition.name]}
                      onDraftChange={(draft) => setDrafts((current) => ({ ...current, [definition.name]: draft }))}
                    />
                  ))}
                </div>
              )}
            </section>
          )
        })}
      </div>
    </Blade>
  )
}

interface FilterRowProps {
  atsSystem: string
  definition: AtsFilterDefinition
  highlighted: boolean
  referenceItems: Parameters<typeof FilterValueControl>[0]["referenceItems"]
  draft?: string
  onDraftChange: (draft: string) => void
}

function FilterRow({ atsSystem, definition, highlighted, referenceItems, draft, onDraftChange }: FilterRowProps) {
  const { filterValues, setFilterValues } = useExplorerContext()
  const { t } = useTranslation()
  const state = filterValues[definition.name]
  const isActive = hasFilterState(state)
  const operator = operatorOf(filterValues, definition)

  return (
    <div
      id={`filter-${definition.name}`}
      className={`rounded-lg border p-2.5 transition-colors ${isActive ? "border-[var(--th-active-pill-border)] bg-accent/40" : "border-card-border"} ${highlighted ? "ring-2 ring-ring" : ""}`}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold text-foreground" title={definition.name}>
          {definition.displayName}
          {definition.isRequired && <span className="ml-0.5 text-destructive">*</span>}
        </span>
        {isActive && !definition.isRequired && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            title={t("explorer.filters.clearOne")}
            onClick={() => setFilterValues(withoutFilter(filterValues, definition.name))}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="flex gap-1.5">
        <OperatorSelect definition={definition} value={operator} onChange={(op) => setFilterValues(withFilterOperator(filterValues, definition, op))} />
        <div className="min-w-0 flex-1">
          <FilterValueControl
            atsSystem={atsSystem}
            definition={definition}
            state={state}
            operator={operator}
            referenceItems={referenceItems}
            onChange={(values, labels) => setFilterValues(withFilterValues(filterValues, definition, values, labels))}
            draft={draft}
            onDraftChange={onDraftChange}
          />
        </div>
      </div>
    </div>
  )
}
